/**
 * Relative-time formatting for the Live Inventory "Updated" label.
 *
 * The backend stores the canonical `inventory_updated_at` in UTC. The
 * frontend renders:
 *   · Main label — relative, e.g. "Updated 2 minutes ago"
 *   · Absolute local time — used for the desktop tooltip AND the
 *     screen-reader / mobile visible-on-tap fallback so users never
 *     need a hover interaction to reach the exact time.
 *
 * Both strings derive from the SAME canonical timestamp, and neither
 * mutates it. `formatRelative` is safe to call every render / on
 * every tick — pure function, no side effects.
 */

const UNITS = [
  { limit: 60,           divisor: 1,        singular: "second", plural: "seconds" },
  { limit: 60 * 60,      divisor: 60,       singular: "minute", plural: "minutes" },
  { limit: 60 * 60 * 24, divisor: 60 * 60,  singular: "hour",   plural: "hours"   },
  { limit: 60 * 60 * 24 * 7, divisor: 60 * 60 * 24, singular: "day",  plural: "days" },
];

/**
 * @param {string|Date|null|undefined} iso   Canonical timestamp
 * @param {Date} [now=new Date()]            Reference time (injectable for tests)
 * @returns {string} Human-readable relative label, e.g. "Updated 2 minutes ago".
 *                   Returns "Just updated" for anything <5s. Falls back to
 *                   "Updated on <local date>" for anything ≥ 7 days.
 */
export function formatRelative(iso, now = new Date()) {
  if (!iso) return "";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffSec = Math.max(0, Math.floor((now.getTime() - d.getTime()) / 1000));
  if (diffSec < 5) return "Just updated";
  for (const u of UNITS) {
    if (diffSec < u.limit) {
      const v = Math.max(1, Math.floor(diffSec / u.divisor));
      return `Updated ${v} ${v === 1 ? u.singular : u.plural} ago`;
    }
  }
  return `Updated on ${d.toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  })}`;
}

/**
 * Absolute local time string — used for tooltips AND for screen readers
 * so mobile / assistive-tech users never depend on hover.
 * @param {string|Date|null|undefined} iso
 * @returns {string} e.g. "Updated Aug 3, 2026, 6:24 PM" (viewer's locale).
 */
export function formatAbsoluteLocal(iso) {
  if (!iso) return "";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `Updated ${d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  })}`;
}
